import {HookClass} from "@dvrt/graphql-widgets";
import {MutableRefObject, useEffect, useRef, useState} from "react";
import {
    CallGroupMembersLazyQueryHookResult,
    CallGroupMembersQuery,
    useCallGroupMembersLazyQuery,
} from "../queries.generated";
import {Member} from "../members/types";

import {CallListLazyQueryHookResult, useCallListLazyQuery} from "@/components/call-list/queries.generated";
import {CallsInterval} from "@/components/call-list/types";
import {GroupType, SegmentType, StateChange} from "@/api";
import {TimeRange, QueueCall, Call} from "./types";
import {getRecordListInterval} from "@/components/IntervalWidget";

import {bin, Bin} from "d3-array";
import {TimeInterval, timeMinute, timeHour, timeDay, timeWeek, timeMonth, timeMonday} from "d3-time";
import {AgentStateLazyQueryHookResult, useAgentStateLazyQuery} from "./queries.generated";

interface RangeDetails {
    from: Date;
    until: Date;
    callThresholds: Date[];
    stateChangeThresholds: Date[];
}

function createRangeDetails(fro: Date, until: Date, scale: TimeInterval): RangeDetails {
    return {
        from: fro,
        until: until,
        callThresholds: scale.range(fro, until),
        stateChangeThresholds: timeDay.range(fro, until),
    };
}

const timeScales: {[key in TimeRange]: () => RangeDetails} = {
    [TimeRange.Today]: () => {
        const until = new Date();
        return createRangeDetails(timeDay.floor(until), until, timeMinute.every(15));
    },
    [TimeRange.Yesterday]: () => {
        const until = timeDay.floor(new Date());
        return createRangeDetails(timeDay.offset(until, -1), until, timeMinute.every(15));
    },
    [TimeRange.ThisWeek]: () => {
        const until = new Date();
        return createRangeDetails(timeMonday(), until, timeHour);
    },
    [TimeRange.PreviousWeek]: () => {
        const until = timeMonday();
        return createRangeDetails(timeWeek.offset(until, -1), until, timeHour);
    },
    [TimeRange.Month]: () => {
        const until = new Date();
        return createRangeDetails(timeMonth.offset(until, -1), until, timeDay);
    },
};

/** Returns a month long interval until today.
 *  If Today is 2025-01-15, the interval will be from 2024-12-16 to 2025-01-15.
 **/
function longestInterval(): CallsInterval {
    const end = new Date();
    return {
        from: timeDay.offset(timeMonth.offset(end, -1), 1),
        to: end,
    };
}

const initialData: Member[] = [];
const initialCallListData: Call[] = [];
const initialStateData: StateChange[] = [];

function binQueueCalls(
    timeRange: TimeRange,
    calls: QueueCall[],
    useStateChangeThresholds?: boolean,
): Bin<QueueCall, Date>[] {
    const rangeDetails = timeScales[timeRange]();
    return bin<QueueCall, Date>()
        .value((d) => new Date(d.received.callTime))
        .domain([rangeDetails.from, rangeDetails.until])
        .thresholds(useStateChangeThresholds ? rangeDetails.stateChangeThresholds : rangeDetails.callThresholds)(calls);
}

function _binStateChanges(timeRange: TimeRange, changes: StateChange[]): Bin<StateChange, Date>[] {
    const rangeDetails = timeScales[timeRange]();
    return bin<StateChange, Date>()
        .value((d) => new Date(d.timeOfChange))
        .domain([rangeDetails.from, rangeDetails.until])
        .thresholds(rangeDetails.stateChangeThresholds)(changes);
}

/**
 * Associates the incoming queue call with the call(s) to the agent.
 */
function associateCalls(calls: Call[]): QueueCall[] {
    // map calls by recording id
    const callsByRecordingId: Record<string, Call[]> = {};
    calls.forEach((c: Call) => {
        if (c.direction == "i") return;
        const curr = callsByRecordingId[c.recordingId];
        if (!curr) callsByRecordingId[c.recordingId] = [c];
        else curr.push(c);
    }, {});

    return calls
        .filter((call) => call.direction === "i")
        .map((call) => ({
            received: call,
            agent: callsByRecordingId[call.recordingId] || [],
        }));
}

/**
 * Data source for call-queue statistics.
 *
 * Fetches all members, the calls and the state changes.
 *
 * Typical call center scenario:
 * - customer calls the auto attendant
 * - the auto attendant does a blind transfer to the queue
 * - the queue calls the agent
 *
 * The generated call records:
 * - customer -> Attendant - segment: normal - the customer selects option in the IVR menu
 * - customer -> Attendant - segment: transfer by callee - segment of the customer call after transfering to the queue
 * - AA -> Queue - segment: transfer by caller - the customer is in orig_src
 * - Queue -> Agent - segment: normal
 *
 * This dataSource only receives the last two records.
 */
export class DataSource extends HookClass {
    public groupId: number;
    private _members: MutableRefObject<Member[]>;
    public queueCalls: MutableRefObject<Call[]>;
    public associatedCalls: MutableRefObject<QueueCall[]>;
    public agentStates: MutableRefObject<StateChange[]>;
    public loaded: boolean;
    private setLoaded: (value: boolean) => void;
    private initialized: MutableRefObject<boolean>;
    private group: MutableRefObject<CallGroupMembersQuery["groups"]["items"][number]>;
    private needAgentStates: boolean;

    private callsQuery: CallListLazyQueryHookResult[0];
    private callGroupMembersQuery: CallGroupMembersLazyQueryHookResult[0];
    private agentStateQuery: AgentStateLazyQueryHookResult[0];

    useHooks(groupId: number, needAgentStates: boolean) {
        [this.loaded, this.setLoaded] = useState<boolean>(false);
        this.needAgentStates = needAgentStates;
        this.groupId = groupId;
        this._members = useRef(initialData);
        this.queueCalls = useRef(initialCallListData);
        this.associatedCalls = useRef([]);
        this.agentStates = useRef(initialStateData);
        this.initialized = useRef(false);
        this.group = useRef(undefined);
        [this.callsQuery] = useCallListLazyQuery();
        [this.callGroupMembersQuery] = useCallGroupMembersLazyQuery();
        [this.agentStateQuery] = useAgentStateLazyQuery();
        useEffect(() => {
            this.fetch();
            return () => {
                this._members.current = initialData;
            };
        }, []);
    }

    get isCallQueue() {
        return this.group.current?.groupType === GroupType.CallQueue;
    }

    get groupInfo() {
        return this.group.current;
    }

    get isInitialized() {
        return this.initialized.current;
    }

    get members() {
        return this._members.current;
    }

    getMemberInfo(numberId: number): Member | undefined {
        return this._members.current.find((m) => m.numberId === numberId);
    }

    binnedCalls(timeRange: TimeRange, useStateChangeThresholds?: boolean): Bin<QueueCall, Date>[] {
        return binQueueCalls(timeRange, this.associatedCalls.current, useStateChangeThresholds);
    }

    binStateChanges(timeRange: TimeRange): Bin<StateChange, Date>[] {
        return _binStateChanges(timeRange, this.agentStates.current);
    }

    get queueCallRecords() {
        // We display only the calls that are to and from the agents.
        // The calls coming from the auto attendant are not displayed.
        const agentIds = this.members.map((m) => m.numberId);
        const domainName = this.group.current.number.domain.name;
        const agentNumbers = this.members.map((m) => `${m.number.shortNumber}@${domainName}`);
        console.log("agent ids", agentIds);
        console.log("agent numbers", agentNumbers);
        return this.queueCalls.current
            .map((call) => {
                // calls from the queue to the agent
                // FIXME maybe we should not check for agent id, because if agent is removed it won't work for old calls
                // - but the next rule relies on this anyway
                if (
                    call.segmentType === SegmentType.Normal &&
                    call.direction !== "i" &&
                    agentIds.includes(call.remoteNumberId)
                ) {
                    console.log("call", call);
                    return call;
                }

                // agent picked up the call
                // here we chose the record that has all the information and that and there the agent is the third party
                if (
                    call.segmentType === SegmentType.TransferByCallee &&
                    call.direction === "i" &&
                    agentNumbers.includes(call.thirdPartyNumber)
                ) {
                    return {
                        ...call,
                        remoteNumber: call.thirdPartyNumber.split("@")[0],
                    };
                }

                return null;
            })
            .filter((call) => call !== null);
    }

    async fetch(filterValue?: string) {
        this.setLoaded(false);

        const {data: groupMembershipsData} = await this.callGroupMembersQuery({
            variables: {
                groupId: this.groupId,
            },
            fetchPolicy: "no-cache",
        });

        this._members.current = groupMembershipsData?.groups?.items[0].members || [];
        this.group.current = groupMembershipsData?.groups?.items[0];

        const {data: queueCallsData} = await this.callsQuery({
            variables: {
                interval: getRecordListInterval(longestInterval()),
                numberId: this.group.current.numberId,
                filterValue: filterValue,
            },
            fetchPolicy: "no-cache",
        });

        this.queueCalls.current = queueCallsData?.callList?.calls || [];
        this.associatedCalls.current = associateCalls(this.queueCalls.current);

        const queryAgentStates = async (groupId: number): Promise<StateChange[]> => {
            const res = await this.agentStateQuery({
                variables: {
                    interval: null,
                    groupId: groupId,
                },
                fetchPolicy: "no-cache",
            });

            if (res.data.agentStateChanges.changes) {
                return res.data.agentStateChanges.changes;
            } else return [];
        };

        if (this.needAgentStates) this.agentStates.current = await queryAgentStates(this.groupId);

        this.initialized.current = true;
        this.setLoaded(true);
    }
}
